import type { ComponentType } from "react";
import { Activity, AlertTriangle, CheckCircle2, ClipboardList, Users, Wallet } from "lucide-react";
import type { AirtimeStats } from "../types";

const THEMES = {
  blue:    { bg: "bg-blue-50",    icon: "bg-blue-100 text-blue-700",       value: "text-blue-900" },
  purple:  { bg: "bg-purple-50",  icon: "bg-purple-100 text-purple-700",   value: "text-purple-900" },
  teal:    { bg: "bg-teal-50",    icon: "bg-teal-100 text-teal-700",       value: "text-teal-900" },
  emerald: { bg: "bg-emerald-50", icon: "bg-emerald-100 text-emerald-700", value: "text-emerald-900" },
  red:     { bg: "bg-red-50",     icon: "bg-red-100 text-red-700",         value: "text-red-900" },
  amber:   { bg: "bg-amber-50",   icon: "bg-amber-100 text-amber-700",     value: "text-amber-900" },
} as const;

type ThemeKey = keyof typeof THEMES;

export default function AirtimeSummaryCards({ stats }: { stats: AirtimeStats }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
      <Card
        title="Employees Configured"
        value={stats.totalEmployeesConfigured}
        icon={Users}
        theme="blue"
      />

      <Card
        title="Monthly Float"
        value={`KES ${stats.totalMonthlyFloat.toLocaleString()}`}
        icon={Wallet}
        theme="purple"
      />

      <Card
        title="Wallet Balance"
        value={`KES ${stats.walletBalance.toLocaleString()}`}
        icon={Activity}
        theme="teal"
      />

      <Card
        title="Wallet Status"
        value={stats.lowWalletBalance ? "Low" : "Healthy"}
        icon={stats.lowWalletBalance ? AlertTriangle : CheckCircle2}
        theme={stats.lowWalletBalance ? "red" : "emerald"}
      />

      <Card
        title="Last Batch"
        value={stats.lastBatchStatus ?? "None"}
        icon={ClipboardList}
        theme="amber"
      />
    </div>
  );
}

function Card({
  title,
  value,
  icon: Icon,
  theme,
}: {
  title: string;
  value: string | number;
  icon: ComponentType<{ className?: string }>;
  theme: ThemeKey;
}) {
  const t = THEMES[theme];
  return (
    <div className={`rounded-3xl border border-slate-200 ${t.bg} p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md`}>
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-slate-500">{title}</p>
          <h3 className={`mt-3 text-2xl font-black ${t.value}`}>{value}</h3>
        </div>

        <div className={`rounded-2xl p-3 ${t.icon}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}
